import React, { memo, useCallback } from 'react';
import { useField } from 'formik';
import DatePicker, { DatePickerProps } from '../DatePicker/DatePicker';
import useTranslation from 'hooks/useTranslation';
import { Dayjs } from 'dayjs';
import {
  DateValidationError,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';

interface FormDatePickerPropsPart {
  name: string;
  onChange?: DatePickerProps['onChange'];
}

export type FormDatePickerProps = FormDatePickerPropsPart &
  Omit<DatePickerProps, keyof FormDatePickerPropsPart | 'value' | 'error'>;

const MemoDatePicker = memo((props: DatePickerProps) => {
  return <DatePicker {...props} />;
});

export default function FormDatePicker({
  name,
  onChange,
  slotProps,
  ...rest
}: FormDatePickerProps) {
  const [{ value, onBlur }, { error, touched }, { setValue, setTouched }] =
    useField<Dayjs | null | undefined>(name);
  const { t } = useTranslation();

  const handleChange = useCallback(
    (
      date: Dayjs | null,
      context: PickerChangeHandlerContext<DateValidationError>,
    ) => {
      !touched && setTouched(true);
      setValue(date);
      onChange?.(date, context);
    },
    [setValue, onChange, touched, setTouched],
  );

  const handleClose = useCallback(() => {
    !touched && setTouched(true);
  }, [touched, setTouched]);

  const showError = !!(error && touched);
  const helperText =
    error &&
    touched &&
    (typeof error === 'string'
      ? error
      : //@ts-ignore
        t(`yup:${error.key}`, error.values));

  return (
    <MemoDatePicker
      {...rest}
      value={value ?? null}
      onChange={handleChange}
      onClose={handleClose}
      slotProps={{
        ...slotProps,
        textField: {
          ...slotProps?.textField,
          name: name,
          onBlur,
          error: showError,
          helperText,
        },
      }}
    />
  );
}
